import React, { useCallback, useEffect, useMemo } from 'react';

import styled from 'styled-components';

import type { CompanySource, SigninRequirements } from '@sine/sine-core-api-types';
import {
  AsyncSingleSelect,
  Form,
  Grid,
  type ISelectOption,
  Input,
  type JoiForm,
  PhoneNumberInput,
  SingleSelect,
  Skeleton,
  spacing,
  useJoiForm,
} from '@sine/sine-ui-components';

import { type ISelectOptionVisitorTypesRequirements } from '../../../helpers/mapToDropdownOptions';
import { useCompaniesSearchScopedMutation } from '../../../shared/hooks/useCompaniesMutation';
import { type SiteOptionWithCountryCode, useSignIn } from '../SigninProvider';
import { checkDetails } from '../helpers/visitorTypeDetailsRequired';
import generateVisitorDetailsFormSchema from '../schemas/visitorDetails.schema';
import type { VisitorDetailsFormValues } from './visitorCheckInModal.types';

const StyledSkeleton = styled(Skeleton)`
  margin-top: ${spacing(3)};
`;

interface VisitorDetailsFormProps {
  formRef?: React.MutableRefObject<JoiForm<VisitorDetailsFormValues> | undefined>;
  pass: {
    invitation?: string;
    firstName: string | undefined;
    lastName: string | undefined;
    email: string;
    mobile: string;
    company: string | undefined;
    companyId: any;
    companySource: any;
    adminNotes: string | null | undefined;
    visitorNotes: string | null | undefined;
    site: any;
    host: any;
    visitorType: string | undefined;
    photoURL: string | null | undefined;
    zone: string | null | undefined;
  };
  firstNameInput: React.RefObject<HTMLInputElement>;
  hideForm: boolean;
}

const getInitialFormValues = (
  formRef: React.MutableRefObject<JoiForm<VisitorDetailsFormValues> | undefined> | undefined,
  pass: {
    invitation?: string;
    firstName?: string;
    lastName?: string;
    email: string;
    mobile: string;
    company?: string;
    companyId?: string;
    companySource?: CompanySource;
    adminNotes?: string | null;
    visitorNotes?: string | null;
    site?: string;
    host?: string;
    visitorType?: string;
    photoURL?: string | null;
    zone?: string | null;
  },
  siteOptions: Array<SiteOptionWithCountryCode>,
  visitorTypeOptions: Array<ISelectOptionVisitorTypesRequirements>,
  hostOptions: Array<ISelectOption>,
  selectedSite?: SiteOptionWithCountryCode | null,
  zoneOptions?: Array<ISelectOption>,
) => {
  return {
    firstName: formRef?.current?.values.firstName || pass.firstName || '',
    lastName: formRef?.current?.values.lastName || pass.lastName || '',
    email: formRef?.current?.values.email || pass.email || '',
    mobile: formRef?.current?.values.mobile || pass.mobile || '',
    company: formRef?.current?.values.company || pass.company || '',
    companyId: formRef?.current?.values.companyId || pass.companyId || null,
    companySource: formRef?.current?.values.companySource || pass.companySource || null,
    site: selectedSite || siteOptions.find((site) => site.value === pass.site) || siteOptions[0] || null,
    visitorType:
      formRef?.current?.values.visitorType ||
      visitorTypeOptions.find((visitorType) => visitorType.value === pass.visitorType) ||
      visitorTypeOptions[0] ||
      null,
    host:
      formRef?.current?.values.host || hostOptions.find((host) => host.value === pass.host) || hostOptions[0] || null,
    zone: formRef?.current?.values.zone || zoneOptions?.find((zone) => zone.value === pass.zone) || null,
  };
};

const isFieldOptional = (
  field: 'email' | 'mobile',
  siteRequirements: SigninRequirements | undefined,
  selectedVisitorTypeOption: ISelectOption | null | undefined,
): boolean => {
  if (!siteRequirements) return true;

  const { detailsRequiredForSignIn, contactDetailsRequiredForSignIn } = siteRequirements;

  const checkField = field === 'email' ? ['mobile', 'none'] : ['email', 'none'];
  return (
    !contactDetailsRequiredForSignIn || checkDetails(detailsRequiredForSignIn, selectedVisitorTypeOption, checkField)
  );
};

export default function VisitorDetailsForm({ formRef, pass, firstNameInput, hideForm }: VisitorDetailsFormProps) {
  const {
    selectedSite,
    siteRequirements,
    siteOptions,
    hostOptions,
    setSelectedSite,
    visitorTypeOptions,
    selectedVisitorType,
    setSelectedVisitorType,
    zoneOptions,
    setSelectedHost,
    zonesEnabled,
    isLoading,
  } = useSignIn();

  const initialValues = useMemo(() => {
    return getInitialFormValues(
      formRef,
      pass,
      siteOptions,
      visitorTypeOptions,
      hostOptions,
      selectedSite,
      zoneOptions?.values,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteOptions]);

  const companyRequired = useMemo(
    () =>
      siteRequirements?.companyRequired &&
      !siteRequirements.visitorTypesExcludedFromCompanyRequired.includes(String(selectedVisitorType?.value) || ''),
    [siteRequirements?.companyRequired, siteRequirements?.visitorTypesExcludedFromCompanyRequired, selectedVisitorType],
  );

  const visitorTypesDetailsRequired = useMemo(() => {
    if (!siteRequirements?.contactDetailsRequiredForSignIn) return undefined;

    return siteRequirements.detailsRequiredForSignIn.find(
      (details) => details.visitorTypeId === selectedVisitorType?.value,
    )?.method;
  }, [
    siteRequirements?.contactDetailsRequiredForSignIn,
    siteRequirements?.detailsRequiredForSignIn,
    selectedVisitorType,
  ]);

  const schema = useMemo(() => {
    return generateVisitorDetailsFormSchema({
      ...siteRequirements,
      emailOrMobileRequired: siteRequirements?.detailsRequiredForSignIn
        ? siteRequirements.contactDetailsRequiredForSignIn && visitorTypesDetailsRequired === 'any'
        : siteRequirements?.emailOrMobileRequired,
      companyRequired,
      visitorTypesDetailsRequired,
      detailsRequiredForSignIn: siteRequirements?.detailsRequiredForSignIn,
    });
  }, [companyRequired, siteRequirements, visitorTypesDetailsRequired]);

  const visitorDetailsForm = useJoiForm(schema, initialValues);

  if (formRef) {
    formRef.current = visitorDetailsForm;
  }

  const selectedSiteOption = visitorDetailsForm.values.site;

  const showHost = useMemo(
    () =>
      hostOptions.length &&
      selectedVisitorType &&
      !!siteRequirements?.hostByVisitorTypeRequired?.includes(selectedVisitorType.value as string),
    [hostOptions.length, selectedVisitorType, siteRequirements?.hostByVisitorTypeRequired],
  );

  const currentCompanyOption = useMemo(
    () => ({
      label: visitorDetailsForm.values.company,
      value: visitorDetailsForm.values.companyId || visitorDetailsForm.values.company,
      sources: visitorDetailsForm.values.companySource,
    }),
    [visitorDetailsForm.values.company, visitorDetailsForm.values.companyId, visitorDetailsForm.values.companySource],
  );

  const { loadVerifiedCompaniesOptions } = useCompaniesSearchScopedMutation();

  const handleFormChange = useCallback(
    (name: string, value?: Record<string, string>) => {
      if (name === 'company' && value) {
        visitorDetailsForm.onChange('company', value.label);
        visitorDetailsForm.onChange('companyId', value.label !== value.value ? value.value : null);
        visitorDetailsForm.onChange('companySource', value.source || 'new');
      } else {
        visitorDetailsForm.onChange(name, value);
      }
    },
    [visitorDetailsForm],
  );

  // Handle visitor type selection based on context:
  // - From pass/invitation: match the pass visitor type or default to first option
  // - Fresh modal: select the first visitor type option
  // - Back navigation: preserve the previously selected visitor type
  useEffect(() => {
    if (!visitorTypeOptions.length || !setSelectedVisitorType) return;

    const matchedOption = visitorTypeOptions.find((visitorType) => visitorType.value === pass.visitorType);

    if (matchedOption) {
      visitorDetailsForm.onChange('visitorType', matchedOption);
      setSelectedVisitorType(matchedOption);
    } else {
      visitorDetailsForm.onChange('visitorType', visitorTypeOptions[0]);
      setSelectedVisitorType(visitorTypeOptions[0]);
    }
  }, [visitorTypeOptions, setSelectedVisitorType, pass.visitorType]);

  useEffect(() => {
    if (!hostOptions.length || !setSelectedHost) return;

    const matchedOption = hostOptions.find((host) => host.value === pass.host);

    if (matchedOption) {
      setSelectedHost(matchedOption);
      visitorDetailsForm.onChange('host', matchedOption);
    } else {
      setSelectedHost(hostOptions[0]);
      visitorDetailsForm.onChange('host', hostOptions[0]);
    }
  }, [hostOptions, setSelectedHost, pass.host]);

  useEffect(() => {
    if (!zoneOptions?.values.length) return;

    // This is necessary to handle cases where the user opens the modal with a prefilled

    const matchedOption = zoneOptions.values.find((zone) => zone.value === pass.zone);

    if (matchedOption) {
      visitorDetailsForm.onChange('zone', matchedOption);
    } else {
      visitorDetailsForm.onChange('zone', zoneOptions.values[0]);
    }
  }, [zoneOptions, pass.zone]);

  useEffect(() => {
    firstNameInput.current?.focus();
  }, []);

  const isEmailOptional = isFieldOptional('email', siteRequirements, selectedVisitorType);
  const isMobileOptional = isFieldOptional('mobile', siteRequirements, selectedVisitorType);

  const handleEmailChange = (value?: string) => {
    const mobileError = visitorDetailsForm.errors.mobile;
    const hasEmailOrMobileError = mobileError?.includes('Email or mobile required') && mobileError.length === 1;

    if (hasEmailOrMobileError) {
      visitorDetailsForm.errors.mobile = [];
    }

    return value;
  };

  const handleMobileChange = (value?: string) => {
    const emailError = visitorDetailsForm.errors.email;
    const hasEmailOrMobileError = emailError?.includes('Email or mobile required') && emailError.length === 1;

    if (hasEmailOrMobileError) {
      visitorDetailsForm.errors.email = [];
    }

    return value;
  };

  if (hideForm) {
    return null;
  }

  return (
    <Form
      formSpacing='compact'
      values={visitorDetailsForm.values}
      errors={visitorDetailsForm.errors}
      onFieldChange={handleFormChange}
      onFieldBlur={visitorDetailsForm.onBlur}
    >
      <Grid container>
        <Grid sm={6} alignItems='stretch'>
          <Input name='firstName' label='First Name' data-st-ext='input-first-name' ref={firstNameInput} />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <Input name='lastName' label='Last Name' data-st-ext='input-last-name' />
        </Grid>

        <Grid sm={12} alignItems='stretch'>
          <AsyncSingleSelect
            data-st-ext='select-companies'
            label='Company'
            name='company'
            value={currentCompanyOption}
            searchFunction={loadVerifiedCompaniesOptions}
            optional={!companyRequired}
            allowFreetext
            onSearchError={() => {}}
            initialOptions={visitorDetailsForm.values.company ? [currentCompanyOption] : undefined}
          />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <Input
            name='email'
            label='Email'
            onChange={handleEmailChange}
            data-st-ext='input-email'
            optional={isEmailOptional}
          />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <PhoneNumberInput
            name='mobile'
            label='Mobile'
            defaultCountryCode={selectedSiteOption?.countryCode}
            data-st-ext='input-mobile'
            onChange={handleMobileChange}
            optional={isMobileOptional}
          />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <SingleSelect
            name='site'
            label='Site'
            options={siteOptions}
            onChange={setSelectedSite}
            data-st-ext='select-site'
          />
        </Grid>

        <Grid sm={6} alignItems='stretch'>
          <SingleSelect
            name='visitorType'
            label='Visitor Type'
            options={visitorTypeOptions}
            data-st-ext='select-visitor-type'
            onChange={setSelectedVisitorType}
            disableSearch
            disableAutosort
          />
        </Grid>

        {isLoading ? (
          <StyledSkeleton width={zonesEnabled ? '383px' : '782px'} height='36px' borderRadius='rounded' />
        ) : showHost ? (
          <Grid sm={zoneOptions?.values.length ? 6 : 12} alignItems='stretch'>
            <SingleSelect
              name='host'
              label='Host'
              options={hostOptions}
              data-st-ext='select-host'
              onChange={setSelectedHost}
            />
          </Grid>
        ) : null}

        {isLoading ? (
          <StyledSkeleton width={zonesEnabled ? '383px' : '782px'} height='36px' borderRadius='rounded' />
        ) : showHost && zonesEnabled && !!zoneOptions?.values.length ? (
          <Grid sm={6} alignItems='stretch'>
            <SingleSelect name='zone' label='Zone' options={zoneOptions.values} data-st-ext='select-zone' />
          </Grid>
        ) : null}
      </Grid>
    </Form>
  );
}
